<template>
  <AIVDialog
    v-model="isVisible"
    title="Edit Clocked In Shift"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal edit-clock-in-modal"
  >
    <div class="edit-clock-in-content">
      <!-- Shift Info Display -->
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p>
          <strong>Date:</strong>
          {{ shift && shift.date ? formatDate(shift.date) : "Loading..." }}
        </p>
        <p
          v-if="
            shift &&
            (shift.originalData?.clockIn ||
              shift.originalData?.clock_in ||
              shift.originalData?.clockOut ||
              shift.originalData?.clock_out ||
              shift.actualStartTime ||
              shift.actualEndTime)
          "
        >
          <strong>Actual Time:</strong>
          {{ formatActualTimeRange(shift) }}
        </p>
        <p>
          <strong>Scheduled Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduledStartTime ||
                shift?.originalData?.scheduled_start ||
                shift?.startTime ||
                shift?.scheduled_start,
              shift?.scheduledEndTime ||
                shift?.originalData?.scheduled_end ||
                shift?.endTime ||
                shift?.scheduled_end,
              shift?.date,
            )
          }}
        </p>
        <p>
          <strong>Payer:</strong>
          {{ shift?.payer || shift?.originalData?.payer || "Not specified" }}
        </p>
        <p
          v-if="
            shift?.payer !== 'Non Service' &&
            shift?.originalData?.payer !== 'Non Service'
          "
        >
          <strong>Client:</strong>
          {{ shift ? formatClient(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Caregiver:</strong>
          <template v-if="isGroupShift && shift && shift.caregivers && shift.caregivers.length > 1">
            {{ shift.caregivers.map(c => `${c.first_name || ''} ${c.last_name || ''}`.trim()).filter(n => n).join(', ') || 'Multiple caregivers' }}
          </template>
          <template v-else>
            {{ shift ? formatCaregiver(shift) : "Loading..." }}
          </template>
        </p>
        <p>
          <strong>Status:</strong>
          <span
            v-if="shift && (shift.status || shift.shift_status)"
            :class="[
              'status-badge',
              getStatusClass(shift.status || shift.shift_status),
            ]"
            >{{ formatStatus(shift.status || shift.shift_status) }}</span
          ><span v-else>Loading...</span>
        </p>
      </div>

      <!-- Per-Caregiver Clock In Times (Group Shifts) -->
      <div v-if="isGroupShift" class="mb-4">
        <h5>Caregiver Clock In Times</h5>
        <div class="caregiver-clock-times">
          <div
            v-for="cg in caregiverClockData"
            :key="cg.caregiverId"
            class="caregiver-clock-row"
          >
            <div class="caregiver-name">{{ cg.caregiverName }}</div>
            <div class="caregiver-time-inputs">
              <div class="caregiver-time-field">
                <span class="clock-label">In:</span>
                <AIVTimeSelect
                  v-model="cg.clockIn"
                  placeholder="Clock in"
                  class="clock-time-picker-sm"
                  :allowExactMinutes="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Assignments (Group Shifts) -->
      <div v-if="isGroupShift && clientTaskData.length > 0" class="mb-4">
        <h5>Client Assignments</h5>
        <div class="client-tasks-list">
          <div
            v-for="task in clientTaskData"
            :key="task.id"
            class="client-task-card"
          >
            <div class="client-task-header">
              {{ task.clientName }}
              <span v-if="task.didNotAttend" class="did-not-attend-badge">
                <i class="fa-solid fa-user-xmark"></i> Did Not Attend
              </span>
            </div>
            <div v-if="!task.didNotAttend" class="client-task-rows">
              <!-- Pick Up -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-car-side"></i>
                  <span class="client-task-label">Pick Up</span>
                </div>
                <div class="client-task-controls">
                  <template v-if="!task.pickupTime">
                    <AIVSelect
                      :value="task.pickupCaregiverId"
                      @input="task.pickupCaregiverId = $event"
                      :options="caregiverOptions"
                      placeholder="Assign caregiver"
                      :custom-style="{ backgroundColor: '#656565', minWidth: '160px' }"
                    />
                  </template>
                  <span v-else class="client-task-completed">
                    {{ getTaskCaregiverName(task.pickupCaregiverId) }} - Completed
                  </span>
                </div>
              </div>
              <!-- Pick Up Location -->
              <div v-if="!task.pickupTime" class="client-task-row location-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Pick Up Location</span>
                </div>
                <div class="client-task-controls location-controls">
                  <AIVSelect
                    v-if="getAddressOptions(task.clientId).length > 0"
                    :value="task.pickupAddressId"
                    @input="task.pickupAddressId = $event"
                    :options="getAddressOptions(task.clientId)"
                    placeholder="Select address"
                    :custom-style="{ backgroundColor: '#656565', width: '100%' }"
                  />
                  <el-button
                    v-if="!isAddingAddress(task.clientId, 'pickup')"
                    type="text"
                    size="small"
                    class="add-address-btn"
                    @click="startAddAddress(task.clientId, 'pickup')"
                  >
                    <i class="fa-solid fa-plus"></i> Add New
                  </el-button>
                </div>
                <div v-if="isAddingAddress(task.clientId, 'pickup')" class="new-address-form">
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address1" placeholder="Address 1" size="small" />
                  </div>
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address2" placeholder="Address 2 (optional)" size="small" />
                  </div>
                  <div class="address-form-row three-col">
                    <el-input v-model="newAddress.city" placeholder="City" size="small" />
                    <el-input v-model="newAddress.state" placeholder="State" size="small" class="state-input" />
                    <el-input v-model="newAddress.zip" placeholder="Zip" size="small" class="zip-input" />
                  </div>
                  <div class="address-form-actions">
                    <el-button size="small" @click="cancelAddAddress">Cancel</el-button>
                    <el-button
                      type="primary"
                      size="small"
                      :loading="savingAddress"
                      :disabled="!newAddress.address1 || !newAddress.city || !newAddress.state"
                      @click="saveNewAddress(task, 'pickup')"
                    >
                      Save Address
                    </el-button>
                  </div>
                </div>
              </div>
              <!-- Drop Off -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Drop Off</span>
                </div>
                <div class="client-task-controls">
                  <template v-if="!task.dropoffTime">
                    <AIVSelect
                      :value="task.dropoffCaregiverId"
                      @input="task.dropoffCaregiverId = $event"
                      :options="caregiverOptions"
                      placeholder="Assign caregiver"
                      :custom-style="{ backgroundColor: '#656565', minWidth: '160px' }"
                    />
                  </template>
                  <span v-else class="client-task-completed">
                    {{ getTaskCaregiverName(task.dropoffCaregiverId) }} - Completed
                  </span>
                </div>
              </div>
              <!-- Drop Off Location -->
              <div v-if="!task.dropoffTime" class="client-task-row location-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Drop Off Location</span>
                </div>
                <div class="client-task-controls location-controls">
                  <AIVSelect
                    v-if="getAddressOptions(task.clientId).length > 0"
                    :value="task.dropoffAddressId"
                    @input="task.dropoffAddressId = $event"
                    :options="getAddressOptions(task.clientId)"
                    placeholder="Select address"
                    :custom-style="{ backgroundColor: '#656565', width: '100%' }"
                  />
                  <el-button
                    v-if="!isAddingAddress(task.clientId, 'dropoff')"
                    type="text"
                    size="small"
                    class="add-address-btn"
                    @click="startAddAddress(task.clientId, 'dropoff')"
                  >
                    <i class="fa-solid fa-plus"></i> Add New
                  </el-button>
                </div>
                <div v-if="isAddingAddress(task.clientId, 'dropoff')" class="new-address-form">
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address1" placeholder="Address 1" size="small" />
                  </div>
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address2" placeholder="Address 2 (optional)" size="small" />
                  </div>
                  <div class="address-form-row three-col">
                    <el-input v-model="newAddress.city" placeholder="City" size="small" />
                    <el-input v-model="newAddress.state" placeholder="State" size="small" class="state-input" />
                    <el-input v-model="newAddress.zip" placeholder="Zip" size="small" class="zip-input" />
                  </div>
                  <div class="address-form-actions">
                    <el-button size="small" @click="cancelAddAddress">Cancel</el-button>
                    <el-button
                      type="primary"
                      size="small"
                      :loading="savingAddress"
                      :disabled="!newAddress.address1 || !newAddress.city || !newAddress.state"
                      @click="saveNewAddress(task, 'dropoff')"
                    >
                      Save Address
                    </el-button>
                  </div>
                </div>
              </div>
              <!-- Shift Notes -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-clipboard"></i>
                  <span class="client-task-label">Shift Notes</span>
                </div>
                <div class="client-task-controls">
                  <template v-if="!task.shiftNotesCompleted">
                    <AIVSelect
                      :value="task.shiftNotesCaregiverId"
                      @input="task.shiftNotesCaregiverId = $event"
                      :options="caregiverOptions"
                      placeholder="Assign caregiver"
                      :custom-style="{ backgroundColor: '#656565', minWidth: '160px' }"
                    />
                  </template>
                  <span v-else class="client-task-completed">
                    {{ getTaskCaregiverName(task.shiftNotesCaregiverId) }} - Completed
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Single Clock In Time (Non-Group Shifts) -->
      <template v-if="!isGroupShift">
        <div class="mb-3">
          <label class="form-label">Clock In Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="newClockInTime"
              placeholder="Select time"
              class="clock-in-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>

        <!-- Break Start Time -->
        <div v-if="hasBreak" class="mb-3">
          <label class="form-label">Break Start Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="breakStartTime"
              placeholder="Select break start time"
              class="clock-in-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>

        <!-- Break End Time -->
        <div v-if="hasBreak" class="mb-3">
          <label class="form-label">Break End Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="breakEndTime"
              placeholder="Select break end time"
              class="clock-in-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>
      </template>

      <!-- Payer Selection -->
      <div class="mb-3">
        <label class="form-label">Payer:</label>
        <AIVSelect
          :value="selectedPayer"
          @input="selectedPayer = $event"
          :options="payerOptions.map((p) => ({ id: p, name: p }))"
          placeholder="Select payer"
          :disabled="isGroupShift"
          :custom-style="{
            backgroundColor: '#656565',
            minWidth: '100%',
          }"
        />
      </div>

      <!-- Client Selection (VR only) -->
      <div v-if="selectedPayer === 'VR'" class="mb-3">
        <label class="form-label">Client:</label>
        <AIVSelect
          :value="selectedClientId"
          @input="handleClientChange"
          :options="availableClients.map((c) => ({ id: c.id, name: c.name }))"
          placeholder="Select client"
          :disabled="loadingClients"
          :custom-style="{
            backgroundColor: '#656565',
            minWidth: '100%',
          }"
        />
      </div>

      <!-- Service Code Selection -->
      <div class="mb-3">
        <label class="form-label">Service Code:</label>
        <AIVSelect
          :value="selectedServiceCodeId"
          @input="selectedServiceCodeId = $event"
          :options="
            availableServiceCodes.map((sc) => ({
              id: sc.id,
              name:
                selectedPayer === 'Non Service' ? sc.name : sc.code || sc.name,
            }))
          "
          placeholder="Select service code"
          :disabled="
            loadingServiceCodes ||
            availableServiceCodes.length === 0 ||
            ((selectedPayer === 'VR' || selectedPayer === 'DSPD') && !selectedClientId)
          "
          :custom-style="{
            backgroundColor: '#656565',
            minWidth: '100%',
          }"
        />
        <div
          v-if="
            !loadingServiceCodes && (selectedPayer === 'VR' || selectedPayer === 'DSPD') && !selectedClientId
          "
          class="service-code-warning"
        >
          <small class="text-warning">
            <i class="fa-solid fa-exclamation-triangle"></i>
            Please select a client first to see available service codes
          </small>
        </div>
        <div
          v-else-if="!loadingServiceCodes && availableServiceCodes.length === 0"
          class="service-code-warning"
        >
          <small class="text-warning">
            <i class="fa-solid fa-exclamation-triangle"></i>
            No service codes available for selected payer
          </small>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Reason for Change</label>
        <el-input
          v-model="changeReason"
          type="textarea"
          :rows="3"
          placeholder="Reason for Change"
          class="w-100"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          @click="handleUpdateClockInTime"
          :loading="updating"
          :disabled="
            isGroupShift
              ? !changeReason.trim()
              : !newClockInTime || !changeReason.trim()
          "
        >
          <i class="fa-solid fa-save"></i> Update Shift
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { nonServiceCodesService } from "@/serviceHandlers/nonServiceCodesService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { isDSPDGroupShift } from "@/utils/shiftStatus";

export default {
  name: "EditClockInTimeModal",
  components: { AIVDialog, AIVTimeSelect, AIVSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      updating: false,
      newClockInTime: null,
      breakStartTime: null,
      breakEndTime: null,
      changeReason: "",
      selectedPayer: null,
      selectedClientId: null,
      selectedServiceCodeId: null,
      payerOptions: ["VR", "DSPD", "Non Service"],
      availableClients: [],
      availableServiceCodes: [],
      loadingServiceCodes: false,
      loadingClients: false,
      initializingPayer: false,
      caregiverClockData: [],
      clientTaskData: [],
      clientAddressOptions: {},
      addingAddressFor: null, // { clientId, type: 'pickup'|'dropoff' }
      newAddress: { address1: "", address2: "", city: "", state: "", zip: "" },
      savingAddress: false,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    isGroupShift() {
      if (!this.shift) return false;
      return isDSPDGroupShift(this.shift) || isDSPDGroupShift(this.shift?.originalData);
    },

    caregiverOptions() {
      if (!this.shift?.caregivers) return [];
      const clockTimes = this.shift.caregiverClockTimes || this.shift.originalData?.caregiverClockTimes || [];
      return this.shift.caregivers.map((c) => {
        let name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
        if (!name) {
          const ct = clockTimes.find((ct) => Number(ct.id_users) === Number(c.id));
          if (ct?.caregiver) {
            name = `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim();
          }
        }
        return { id: c.id, name: name || "Unknown" };
      });
    },

    hasBreak() {
      // Check if shift has any breaks
      const breaks =
        this.shift?.breaks || this.shift?.originalData?.breaks || [];
      return breaks.length > 0;
    },
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        if (this.isGroupShift) {
          this.initializeCaregiverClockData();
          this.initializeClientTaskData();
        } else {
          this.initializeTime();
          this.initializeBreakTimes();
        }
        this.initializePayerAndServiceCode();
      }
    },
    async selectedPayer(newPayer, oldPayer) {
      // Skip if we're in the middle of initialization
      if (this.initializingPayer) return;

      if (newPayer === "VR") {
        // Load clients when VR is selected
        await this.loadClients();
      } else {
        // Clear client selection when not VR
        this.selectedClientId = null;
        this.availableClients = [];
      }

      if (newPayer) {
        this.loadAvailableServiceCodes();
      }
    },
    selectedClientId(newClientId) {
      // Reload service codes when client changes
      if ((this.selectedPayer === "VR" || this.selectedPayer === "DSPD") && newClientId) {
        this.loadAvailableServiceCodes();
      }
    },
  },

  methods: {
    resetForm() {
      this.newClockInTime = null;
      this.breakStartTime = null;
      this.breakEndTime = null;
      this.changeReason = "";
      this.selectedPayer = null;
      this.selectedClientId = null;
      this.selectedServiceCodeId = null;
      this.availableClients = [];
      this.availableServiceCodes = [];
      this.caregiverClockData = [];
      this.clientTaskData = [];
      this.clientAddressOptions = {};
      this.addingAddressFor = null;
      this.newAddress = { address1: "", address2: "", city: "", state: "", zip: "" };
      this.savingAddress = false;
    },

    initializeCaregiverClockData() {
      const caregivers = this.shift.caregivers || [];
      const clockTimes = this.shift.caregiverClockTimes || this.shift.originalData?.caregiverClockTimes || [];

      this.caregiverClockData = caregivers
        .map((caregiver) => {
          const ct = clockTimes.find(
            (c) => Number(c.id_users) === Number(caregiver.id),
          );

          // Only include caregivers who are clocked in
          if (!ct?.clock_in) return null;

          let name = `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
          if (!name && ct?.caregiver) {
            name = `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim();
          }

          return {
            caregiverId: caregiver.id,
            caregiverName: name || "Unknown",
            clockIn: ct.clock_in ? this.convertUTCDateTimeToLocalTime(ct.clock_in) : null,
          };
        })
        .filter(Boolean);
    },

    initializeClientTaskData() {
      const clientDetails = this.shift.clientDetails || this.shift.originalData?.clientDetails || [];
      const shiftClients = this.shift.clients || this.shift.originalData?.clients || [];

      // Build task data from clientDetails entries
      const taskData = clientDetails.map((cd) => {
        let clientName = "Unknown Client";
        if (cd.client) {
          clientName = `${cd.client.first_name || ""} ${cd.client.last_name || ""}`.trim() || "Unknown Client";
        } else if (cd.id_clients) {
          const client = shiftClients.find((c) => Number(c.id) === Number(cd.id_clients));
          if (client) {
            clientName = `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unknown Client";
          }
        }

        return {
          id: cd.id,
          clientName,
          clientId: cd.id_clients || cd.client_id || cd.client?.id,
          pickupCaregiverId: cd.pickup_caregiver_id,
          pickupTime: cd.pickup_time,
          pickupAddressId: cd.pickup_address_id || null,
          dropoffCaregiverId: cd.dropoff_caregiver_id,
          dropoffTime: cd.dropoff_time,
          dropoffAddressId: cd.dropoff_address_id || null,
          shiftNotesCaregiverId: cd.shift_notes_caregiver_id,
          shiftNotesCompleted: cd.shift_notes_completed,
          didNotAttend: cd.did_not_attend || false,
        };
      });

      // Add entries for any clients that don't have a clientDetails row yet
      const coveredClientIds = new Set(taskData.map((t) => Number(t.clientId)).filter(Boolean));
      shiftClients.forEach((client) => {
        if (!coveredClientIds.has(Number(client.id))) {
          const clientName = `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unknown Client";
          taskData.push({
            id: `new-${client.id}`,
            clientName,
            clientId: client.id,
            pickupCaregiverId: null,
            pickupTime: null,
            pickupAddressId: null,
            dropoffCaregiverId: null,
            dropoffTime: null,
            dropoffAddressId: null,
            shiftNotesCaregiverId: null,
            shiftNotesCompleted: false,
          });
        }
      });

      this.clientTaskData = taskData;

      // Load client addresses for pickup/dropoff location editing
      this.loadClientAddresses();
    },

    formatAddressOption(addr) {
      if (!addr) return "";
      const parts = [addr.address1, addr.city, addr.state, addr.zip];
      return parts.filter(Boolean).join(", ");
    },

    getAddressOptions(clientId) {
      return this.clientAddressOptions[clientId] || [];
    },

    async loadClientAddresses() {
      const clientIds = [...new Set(this.clientTaskData.map((t) => t.clientId).filter(Boolean))];
      for (const clientId of clientIds) {
        try {
          const client = await clientsService.getById(clientId);
          const addrs = client.addresses && client.addresses.length > 0
            ? client.addresses
            : client.address
              ? [client.address]
              : [];
          const options = addrs.map((a) => ({
            id: a.id,
            name: this.formatAddressOption(a),
          }));
          this.$set(this.clientAddressOptions, clientId, options);

          // Auto-select address for tasks that don't have one set,
          // or where the current value doesn't match any available option
          if (options.length > 0) {
            const optionIds = options.map((o) => o.id);
            const defaultId = options[0].id;
            this.clientTaskData.forEach((task) => {
              if (Number(task.clientId) === Number(clientId)) {
                if (!task.pickupAddressId || !optionIds.includes(task.pickupAddressId)) {
                  this.$set(task, "pickupAddressId", defaultId);
                }
                if (!task.dropoffAddressId || !optionIds.includes(task.dropoffAddressId)) {
                  this.$set(task, "dropoffAddressId", defaultId);
                }
              }
            });
          }
        } catch (err) {
          this.$set(this.clientAddressOptions, clientId, []);
        }
      }
    },

    isAddingAddress(clientId, type) {
      return (
        this.addingAddressFor &&
        this.addingAddressFor.clientId === clientId &&
        this.addingAddressFor.type === type
      );
    },

    startAddAddress(clientId, type) {
      this.addingAddressFor = { clientId, type };
      this.newAddress = { address1: "", address2: "", city: "", state: "", zip: "" };
    },

    cancelAddAddress() {
      this.addingAddressFor = null;
      this.newAddress = { address1: "", address2: "", city: "", state: "", zip: "" };
    },

    async saveNewAddress(task, type) {
      const clientId = task.clientId;
      this.savingAddress = true;
      try {
        // Fetch the current client data
        const client = await clientsService.getById(clientId);
        const existingAddresses = client.addresses || [];

        // Add the new address to the client's addresses array
        const updatedAddresses = [
          ...existingAddresses,
          { ...this.newAddress },
        ];

        // Save to the client profile
        await clientsService.update(clientId, {
          ...client,
          addresses: updatedAddresses,
        });

        // Refresh the address options for this client
        const refreshedClient = await clientsService.getById(clientId);
        const addrs =
          refreshedClient.addresses && refreshedClient.addresses.length > 0
            ? refreshedClient.addresses
            : [];
        const options = addrs.map((a) => ({
          id: a.id,
          name: this.formatAddressOption(a),
        }));
        this.$set(this.clientAddressOptions, clientId, options);

        // Select the newly added address (last one in the list)
        if (options.length > 0) {
          const newAddrId = options[options.length - 1].id;
          if (type === "pickup") {
            task.pickupAddressId = newAddrId;
          } else {
            task.dropoffAddressId = newAddrId;
          }
        }

        this.cancelAddAddress();
        this.$notifySuccess("Address added successfully.");
      } catch (error) {
        this.$notifyError(
          "Error adding address: " +
            (error.response?.data?.message || error.message),
        );
      } finally {
        this.savingAddress = false;
      }
    },

    getTaskCaregiverName(caregiverId) {
      if (!caregiverId) return "Unknown";

      if (this.shift?.caregivers) {
        const caregiver = this.shift.caregivers.find(
          (c) => Number(c.id) === Number(caregiverId),
        );
        if (caregiver) {
          const name = `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
          if (name) return name;
        }
      }

      const clockTimes = this.shift?.caregiverClockTimes || this.shift?.originalData?.caregiverClockTimes || [];
      const ct = clockTimes.find((c) => Number(c.id_users) === Number(caregiverId));
      if (ct?.caregiver) {
        const name = `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim();
        if (name) return name;
      }

      return "Unknown";
    },

    initializeTime() {
      if (!this.shift) return;

      // Set default to current clock in time if available
      const clockInTime =
        this.shift.originalData?.clockIn ||
        this.shift.originalData?.clock_in ||
        this.shift.actualStartTime;
      if (clockInTime) {
        // Convert UTC datetime to local time, same pattern as ShiftModal
        const localTime = this.convertUTCDateTimeToLocalTime(clockInTime);
        this.newClockInTime = localTime;
      } else {
        // Default to current local time
        const now = new Date();
        this.newClockInTime = now.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    },

    initializeBreakTimes() {
      if (!this.shift) return;

      // Get breaks array from shift
      const breaks =
        this.shift?.breaks || this.shift?.originalData?.breaks || [];

      if (breaks.length > 0) {
        // Get the first break (usually there's only one)
        const breakRecord = breaks[0];

        // Initialize break start time
        if (breakRecord.break_start) {
          const localTime = this.convertUTCDateTimeToLocalTime(
            breakRecord.break_start,
          );
          this.breakStartTime = localTime;
        }

        // Initialize break end time
        if (breakRecord.break_end) {
          const localTime = this.convertUTCDateTimeToLocalTime(
            breakRecord.break_end,
          );
          this.breakEndTime = localTime;
        }
      }
    },

    async initializePayerAndServiceCode() {
      if (!this.shift) return;

      // Set flag to prevent watcher from triggering during initialization
      this.initializingPayer = true;

      try {
        // Set current payer - for DSPD group shifts, force DSPD
        if (this.isGroupShift) {
          this.selectedPayer = "DSPD";
        } else {
          this.selectedPayer =
            this.shift.payer || this.shift.originalData?.payer || "VR";
        }

        // Load clients and set current client for VR and DSPD payers
        if (this.selectedPayer === "VR" || this.selectedPayer === "DSPD") {
          if (this.selectedPayer === "VR") {
            await this.loadClients();
          }
          const clientId = this.getClientId();
          if (clientId) {
            this.selectedClientId = clientId;
          }
        }

        // Load service codes based on payer
        await this.loadAvailableServiceCodes();

        // Set current service code if available
        // Check the appropriate code array based on payer type
        const shiftServiceCodes =
          this.selectedPayer === "Non Service"
            ? this.shift.non_service_codes ||
              this.shift.originalData?.non_service_codes ||
              []
            : this.shift.service_codes ||
              this.shift.originalData?.service_codes ||
              [];

        if (shiftServiceCodes.length > 0) {
          this.selectedServiceCodeId = shiftServiceCodes[0].id;
        }
      } finally {
        // Always clear the flag
        this.initializingPayer = false;
      }
    },

    async loadClients() {
      this.loadingClients = true;
      try {
        const clients = await clientsService.list({ slim: true });
        this.availableClients = (clients || []).map((client) => ({
          id: Number(client.id) || client.id,
          name: `${client.first_name} ${client.last_name}`,
        }));
      } catch (error) {
        this.availableClients = [];
        this.$notifyError("Failed to load clients");
      } finally {
        this.loadingClients = false;
      }
    },

    handleClientChange(clientId) {
      this.selectedClientId = clientId;
      // Clear service code selection when client changes
      this.selectedServiceCodeId = null;
    },

    async loadAvailableServiceCodes() {
      if (!this.selectedPayer) return;

      this.loadingServiceCodes = true;
      try {
        if (this.selectedPayer === "Non Service") {
          const codes = await nonServiceCodesService.list();
          this.availableServiceCodes = codes || [];
        } else if (this.selectedPayer === "VR") {
          // For VR payer, fetch service codes by client
          if (this.selectedClientId) {
            const codes = await serviceCodesService.getByClient({
              clientId: this.selectedClientId,
              payer_type: "VR",
            });
            this.availableServiceCodes = codes || [];
          } else {
            // No client selected, don't load service codes yet
            this.availableServiceCodes = [];
          }
        } else if (this.selectedPayer === "DSPD") {
          // For DSPD payer, fetch service codes by client auth
          if (this.selectedClientId) {
            const codes = await serviceCodesService.getByClient({
              clientId: this.selectedClientId,
              payer_type: "DSPD",
            });
            let filtered = codes || [];
            // For 1-on-1 shifts, exclude group codes (DSP, DSG)
            if (!this.isGroupShift) {
              filtered = filtered.filter(sc => sc.code !== 'DSP' && sc.code !== 'DSG');
            }
            this.availableServiceCodes = filtered;
          } else {
            this.availableServiceCodes = [];
          }
        } else {
          // For other payers (Private), load all service codes
          const codes = await serviceCodesService.list();
          this.availableServiceCodes = codes || [];
        }

        // Auto-select if only one option
        if (this.availableServiceCodes.length === 1) {
          this.selectedServiceCodeId = this.availableServiceCodes[0].id;
        }
      } catch (error) {
        this.availableServiceCodes = [];
        this.$notifyError("Failed to load service codes");
      } finally {
        this.loadingServiceCodes = false;
      }
    },

    getClientId() {
      // Helper method to get client ID from the shift object
      if (!this.shift) return null;

      // Check various possible locations for client ID
      return (
        this.shift.clientId ||
        this.shift.client_id ||
        this.shift.originalData?.clientId ||
        this.shift.originalData?.client_id ||
        (this.shift.clients && this.shift.clients.length > 0
          ? this.shift.clients[0].id
          : null)
      );
    },

    convertUTCDateTimeToLocalTime(utcDateTimeStr) {
      if (!utcDateTimeStr) return "";

      // Parse the datetime string - it should already be a proper UTC timestamp from backend
      const utcDate = new Date(utcDateTimeStr);
      if (isNaN(utcDate.getTime())) return "";

      // Convert to local time string (HH:MM format)
      return utcDate.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    async handleUpdateClockInTime() {
      // For group shifts, handle per-caregiver clock-in updates
      if (this.isGroupShift) {
        if (!this.changeReason.trim()) {
          this.$notifyError("Please provide a reason for the change.");
          return;
        }
        if (!this.selectedServiceCodeId) {
          this.$notifyError("Please select a service code.");
          return;
        }

        await this.handleGroupShiftClockInUpdate();
        return;
      }

      if (!this.newClockInTime || !this.changeReason.trim()) {
        this.$notifyError(
          "Please select a new clock in time and provide a reason for the change.",
        );
        return;
      }

      if (!this.selectedPayer) {
        this.$notifyError("Please select a payer.");
        return;
      }

      if (!this.selectedServiceCodeId) {
        this.$notifyError("Please select a service code.");
        return;
      }

      if (!this.shift || !this.shift.date) {
        this.$notifyError("Shift data is not available. Please try again.");
        return;
      }

      this.updating = true;
      try {
        // Convert the time back to a full datetime by combining with the shift date
        // Parse the time input (HH:MM format)
        const [hours, minutes] = this.newClockInTime.split(":").map(Number);

        // Create a new date object using the shift date but in local timezone
        const [year, month, day] = this.shift.date.split("-").map(Number);
        const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

        const payload = {
          clock_in: localDate.toISOString(),
          payer: this.selectedPayer,
          clientId: this.selectedClientId,
          serviceCodeIds: [this.selectedServiceCodeId],
          adminChangeReason: this.changeReason.trim(),
        };

        // Include break times if they exist
        if (this.hasBreak) {
          if (this.breakStartTime) {
            const [breakHours, breakMinutes] = this.breakStartTime
              .split(":")
              .map(Number);
            const breakStartDate = new Date(
              year,
              month - 1,
              day,
              breakHours,
              breakMinutes,
              0,
              0,
            );
            payload.break_start = breakStartDate.toISOString();
          }

          if (this.breakEndTime) {
            const [breakHours, breakMinutes] = this.breakEndTime
              .split(":")
              .map(Number);
            const breakEndDate = new Date(
              year,
              month - 1,
              day,
              breakHours,
              breakMinutes,
              0,
              0,
            );
            payload.break_end = breakEndDate.toISOString();
          }
        }

        await shiftsService.update(
          this.shift.originalData?.id || this.shift.id,
          payload,
        );

        this.$notifySuccess("Shift updated successfully!");
        this.isVisible = false;
        this.resetForm();
        this.$emit("clock-in-updated", {
          shiftId: this.shift.id,
          newClockInTime: this.newClockInTime,
        });
      } catch (error) {
        this.$notifyError(
          "Error updating clock in time: " +
            (error.response?.data?.message || error.message),
        );
      } finally {
        this.updating = false;
      }
    },

    async handleGroupShiftClockInUpdate() {
      this.updating = true;
      try {
        const shiftId = this.shift.originalData?.id || this.shift.id;

        let dateStr = this.shift.date;
        if (dateStr.includes("T")) {
          dateStr = dateStr.split("T")[0];
        }
        const [year, month, day] = dateStr.split("-").map(Number);

        // Build per-caregiver clock times payload (clock_in only)
        const caregiverClockTimes = this.caregiverClockData
          .filter((cg) => cg.clockIn)
          .map((cg) => {
            const [inH, inM] = cg.clockIn.split(":").map(Number);
            const localIn = new Date(year, month - 1, day, inH, inM, 0, 0);

            return {
              id_users: cg.caregiverId,
              clock_in: localIn.toISOString(),
            };
          });

        const payload = {
          payer: this.selectedPayer,
          serviceCodeIds: [this.selectedServiceCodeId],
          adminChangeReason: this.changeReason.trim(),
          caregiverClockTimes: caregiverClockTimes,
        };

        await shiftsService.update(shiftId, payload);

        // Update client details (caregiver assignments) if any client task data exists
        if (this.clientTaskData.length > 0) {
          const clientDetailsPayload = this.clientTaskData.map((task) => ({
            clientDetailId: task.id,
            clientId: task.clientId,
            pickupCaregiverId: task.pickupCaregiverId,
            pickupTime: task.pickupTime || null,
            pickupAddressId: task.pickupAddressId || null,
            dropoffCaregiverId: task.dropoffCaregiverId,
            dropoffTime: task.dropoffTime || null,
            dropoffAddressId: task.dropoffAddressId || null,
            shiftNotesCaregiverId: task.shiftNotesCaregiverId,
          }));

          await shiftsService.adminUpdateClientDetails(shiftId, {
            clientDetails: clientDetailsPayload,
            adminChangeReason: this.changeReason.trim(),
          });
        }

        this.$notifySuccess("Group shift updated successfully!");
        this.isVisible = false;
        this.resetForm();
        this.$emit("clock-in-updated", { shiftId: this.shift.id });
      } catch (error) {
        this.$notifyError(
          "Error updating group shift: " +
            (error.response?.data?.message || error.message),
        );
      } finally {
        this.updating = false;
      }
    },

    formatClockInTime(dateTimeString) {
      if (!dateTimeString) return "";

      // Use the same conversion logic to get local time
      const localTime = this.convertUTCDateTimeToLocalTime(dateTimeString);

      // Convert HH:MM to 12-hour format for display
      if (!localTime) return "";

      const [hours, minutes] = localTime.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;

      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    },

    formatTime(timeString) {
      if (!timeString) return "";

      const localTimeString = this.convertUTCTimeToLocalTime(timeString);
      if (!localTimeString) return "";

      const timeParts = localTimeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";

      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;

      const formattedMinutes = minutes === "00" ? "00" : minutes;
      return `${hours}:${formattedMinutes}${ampm}`;
    },

    convertUTCTimeToLocalTime(timeStr) {
      if (!timeStr || !this.shift?.date) return timeStr;

      try {
        const shiftDate = this.shift.date.split("T")[0];
        const utcDateTime = `${shiftDate}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (isNaN(utcDate.getTime())) {
          return timeStr;
        }

        return utcDate.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return timeStr;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      // Handle date string to avoid timezone issues
      let dateStr = dateString;
      if (dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0]; // Extract just the date part
      }

      // Create date object with explicit local timezone to avoid UTC interpretation
      const [year, month, day] = dateStr.split("-");
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes("T")) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as UTC
      if (date) {
        const dateOnly = date.split("T")[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },

    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes("T")) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      // Fallback: format as simple time strings
      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "";

      // Use the same conversion logic
      const localDate = this.convertUTCDateTimeToLocal(dateTimeString);

      return localDate.toLocaleString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatStatus(status) {
      if (!status) return "";
      if (status.toLowerCase() === "error") return "Clock Out Error";
      return status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ");
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        return shift.caregivers[0].name;
      }
      return shift.caregiver || "Unassigned";
    },

    getStatusClass(status) {
      if (!status) return "badge-secondary";

      const normalizedStatus = status.toLowerCase().replace(/\s+/g, "-");

      switch (normalizedStatus) {
        case "unassigned":
          return "badge-secondary";
        case "scheduled":
          return "badge-info";
        case "clocked-in":
        case "clocked in":
          return "badge-success";
        case "clocked-out":
        case "clocked out":
          return "badge-warning";
        case "approved":
          return "badge-primary";
        case "available":
          return "badge-success";
        case "missed":
          return "badge-secondary";
        case "error":
          return "badge-secondary";
        default:
          return "badge-info";
      }
    },

    formatActualTimeRange(shift) {
      // Handle both snake_case and camelCase formats
      const clockIn =
        shift.originalData?.clock_in ||
        shift.originalData?.clockIn ||
        shift.actualStartTime;
      const clockOut =
        shift.originalData?.clock_out ||
        shift.originalData?.clockOut ||
        shift.actualEndTime;

      if (!clockIn && !clockOut) {
        return "Not clocked in";
      }

      const clockInTime = clockIn ? this.formatClockInTime(clockIn) : "";
      const clockOutTime = clockOut ? this.formatClockInTime(clockOut) : "";

      if (clockIn && clockOut) {
        return `${clockInTime} - ${clockOutTime}`;
      } else if (clockIn) {
        return `${clockInTime} - In Progress`;
      } else {
        return "Not clocked in";
      }
    },
  },
};
</script>

<style scoped>
.edit-clock-in-content {
  max-width: 700px;
  min-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.edit-clock-in-content .mb-3 {
  margin-bottom: 1.5rem;
}

.edit-clock-in-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.edit-clock-in-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.edit-clock-in-content strong {
  color: #fff;
  font-weight: 600;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

.time-input-container {
  margin-bottom: 0.5rem;
}

.clock-in-picker {
  width: 100%;
}

.text-center {
  text-align: center;
}

/* Status badge styling matching ShiftActionModal */
.status-badge {
  display: inline-block !important;
  padding: 0.35rem 0.75rem !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  border-radius: 1rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.status-badge.badge-secondary {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.7),
    rgba(220, 53, 69, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-info {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-success {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  color: #000 !important;
}

.status-badge.badge-warning {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-primary {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ) !important;
  color: #fff !important;
}

/* Make placeholder text darker when textarea is focused/selected */
:deep(.el-textarea__inner:focus::placeholder) {
  color: #999 !important;
}

.service-code-warning {
  margin-top: 0.5rem;
}

.service-code-warning .text-warning {
  color: #ffc107 !important;
}

.service-code-warning i {
  margin-right: 0.25rem;
}

/* Section headings for group shift sections */
.edit-clock-in-content .mb-4 {
  margin-bottom: 2rem;
}

/* Caregiver Clock Times */
.caregiver-clock-times {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.caregiver-clock-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.caregiver-name {
  font-weight: 600;
  color: #fff;
  min-width: 140px;
}

.caregiver-time-inputs {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.caregiver-time-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clock-label {
  color: #909399;
  font-size: 0.85rem;
  white-space: nowrap;
}

.clock-time-picker-sm {
  width: 140px;
}

/* Client Tasks */
.client-tasks-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.client-task-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
}

.client-task-header {
  font-weight: 600;
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.did-not-attend-badge {
  font-size: 0.8rem;
  font-weight: 500;
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.15);
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
}

.client-task-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.client-task-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.client-task-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff;
}

.client-task-info i {
  font-size: 14px;
  color: #909399;
  width: 18px;
  text-align: center;
}

.client-task-label {
  font-weight: 500;
  font-size: 0.9rem;
}

.client-task-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.client-task-controls.location-controls {
  flex: 1;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.location-row {
  flex-wrap: wrap;
}

.add-address-btn {
  color: #6ba6cd !important;
  padding: 4px 8px !important;
  white-space: nowrap;
}

.add-address-btn:hover {
  color: #8fc0e0 !important;
}

.new-address-form {
  width: 100%;
  padding: 0.75rem;
  margin-top: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(107, 166, 205, 0.3);
  border-radius: 6px;
}

.address-form-row {
  margin-bottom: 0.5rem;
}

.address-form-row.three-col {
  display: flex;
  gap: 0.5rem;
}

.address-form-row.three-col .state-input {
  max-width: 80px;
}

.address-form-row.three-col .zip-input {
  max-width: 100px;
}

.address-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.client-task-completed {
  color: #67c23a;
  font-size: 0.85rem;
}

@media (max-width: 768px) {
  /* Make modal wider on mobile devices */
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .edit-clock-in-content {
    min-width: auto !important;
    padding: 0 1rem;
  }

  .caregiver-clock-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .caregiver-time-inputs {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  .caregiver-time-field {
    width: 100%;
  }

  .clock-time-picker-sm {
    width: 100%;
  }

  .client-task-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .client-task-controls {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 0.5rem;
  }
}
</style>
